<template>
  <div class="pcPlayer" ref="pcPlayer" :class="$style.playerWrap">
    <div :class="$style.videoPlayer" :style="playerStyle">
      <video ref="videoPlayer" class="video-js" :class="$style.videojs"></video>
    </div>
  </div>
</template>
<script>
import i18n from '@/i18n'
import videojs from '@/components/videojs'
import { mapState } from 'vuex'
import { checkPrivilege } from '@/services/api'
import Emitter from 'element-ui/lib/mixins/emitter'
import { errorCodeMap } from '@/utils/errorcodes.js'
import debounce from 'lodash/debounce'

require('video.js/dist/video-js.css')
require('./style/player.css')
export default {
  name: 'VideoPlayer',
  props: {
    options: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  mixins: [Emitter],
  computed: mapState(['fileInfo']),
  data() {
    return {
      player: null,
      playerStyle: {},
      performanceData: {}
    }
  },
  methods: {
    updatePlayerStyle: debounce(function () {
      // 播放器分辨率为16:9，当浏览器高度过小时，调整播放器宽度
      const $pcPlayer = this.$refs.pcPlayer
      if (!$pcPlayer) { return }
      const { clientWidth, clientHeight } = $pcPlayer
      const horizontalMargin = 150 * 2
      const videoRatio = 16 / 9
      const boxRatio = (clientWidth - horizontalMargin) / clientHeight
      if (boxRatio > videoRatio) {
        // 播放器宽度由容器高度计算
        const playerWidth = ~~(clientHeight * videoRatio)
        this.playerStyle = {
          width: playerWidth + 'px',
          minWidth: 'auto'
        }
      } else {
        this.playerStyle = {}
      }
    }, 200)
  },
  mounted() {
    window.HELP_IMPROVE_VIDEOJS = false
    this.updatePlayerStyle()
    window.addEventListener('resize', this.updatePlayerStyle)
    this.player = videojs(this.$refs.videoPlayer, this.options, () => {
      this.dispatch('APP', 'dwReporter', {
        page: 'preview',
        operation: 'show'
      })
      this.player.controlBar.removeChild('PictureInPictureToggle')
      this.player.volume(0.5)
      this.player.tech_.on('retryplaylist', () => {
        let previewType = this.$route.params.sid ? 'link' : 'normal'
        checkPrivilege(this.fileInfo.groupid, previewType).catch((err) => {
          // 当权益接口非200时则表示权益过期，跳转到错误页面
          if (err.detail.type === 'SERVICE_ERROR') {
            this.dispatch('APP', 'showError', {
              info: err.detail.info,
              val: {
                type: i18n.t('79be6d4e4c')
              }
            })
          }
        })
      })
      this.player.tech_.on('play', () => {
        this.dispatch('APP', 'dwReporter', {
          page: 'preview',
          operation: 'play',
          play_time: this.$refs.videoPlayer.currentTime.toFixed(2)
        })
        this.$DwManager.recordPlayTime('play')
        this.$DwManager.reportUserActiveAction('play', 'video')
      })
      this.player.tech_.on('pause', () => {
        this.dispatch('APP', 'dwReporter', {
          page: 'preview',
          operation: 'play',
          play_time: this.$refs.videoPlayer.currentTime.toFixed(2)
        })

        this.$DwManager.recordPlayTime('pause')
        this.$DwManager.reportUserActiveAction('pause', 'video')
      })
    })
    this.player.on('fullscreenchange', () => {
      // this.player.isFullscreen() f11退出全屏时不准确
      if (
        document.fullscreen ||
        document.mozFullScreen ||
        document.webkitIsFullScreen
      ) {
        this.dispatch('APP', 'dwReporter', {
          page: 'preview',
          operation: 'fullscreen',
          play_time: this.$refs.videoPlayer.currentTime.toFixed(2)
        })
        this.$DwManager.reportUserActiveAction('fullscreen', 'video')
      }
    })
    this.player.on('volumechange', (evt) => {
      if (this.$refs.videoPlayer.muted) {
        this.dispatch('APP', 'dwReporter', {
          page: 'preview',
          operation: 'mute',
          play_time: this.$refs.videoPlayer.currentTime.toFixed(2)
        })
      }
      this.$DwManager.reportUserActiveAction('volumechange', 'video')
    })
    // 绑定空格播放/暂停
    this.player.on('keydown', (evt) => {
      let key = evt.which || evt.keyCode
      if (key === 32 || key === 179) {
        event.preventDefault()
        if (this.player.paused()) {
          this.player.play()
        } else {
          this.player.pause()
        }
      }
    })
    this.player.on('error', () => {
      // IE10及以下版本不支持播放
      this.dispatch('APP', 'showError', {
        info: {
          code: errorCodeMap.FILETYPE_NOT_SUPPORT
        }
      })
      this.$DwManager.recordInitialPlayStatus(this.$refs.videoPlayer.error.code)
    })

    // dw start
    this.player.on('canplaythrough', () => {
      this.$DwManager.measureFileLoaded('end')
    })
    this.player.on('loadedmetadata', () => {
      this.$DwManager.reportShowAction('video', {
        fileInfo: this.fileInfo,
        mediaInfo: {
          duration: this.player.duration(),
          width: this.player.videoWidth(),
          height: this.player.videoHeight(),
          poster: this.options.poster
        }
      })
      this.$DwManager.recordTotalTime(this.player.duration())
    })

    this.player.on('waiting', () => {
      // 缓冲不足时记录当前时间
      this.player.__buffering = true
      this.$DwManager.measureFirstTimeBuffering('start')
    })
    this.player.on('canplay', () => {
      // 恢复播放时清除标记
      this.player.__buffering = false
    })
    this.player.on('playing', () => {
      this.$DwManager.measureFirstTimeBuffering('end')
    })

    this.player.on('loadstart', () => {
      this.$DwManager.measureFirstFrameLoaded('start')
      this.$DwManager.measureFileLoaded('start')
    })

    this.player.on('loadeddata', () => {
      this.$DwManager.measureFirstFrameLoaded('end')
    })

    this.player.on('ended', () => {
      this.$DwManager.recordPlayTime('ended')
    })

    // 自定义进度条更新逻辑
    this.player.on('timeupdate', () => {
      if (this.player.__buffering) {
        // 暂停时不更新进度条
        if (this.player.controlBar.progressControl.timeDivider) {
          this.player.controlBar.progressControl.timeDivider.el_.innerHTML = this.player.__lastTime || '0:00'
          this.player.controlBar.progressControl.loadProgressBar.el_.style.width = '0%'
          return
        }
      }
      // 正常更新进度
      this.player.__lastTime = this.player.currentTime()
    })

    this.player.on('seeked', () => {
      this.$DwManager.reportUserActiveAction('seeking', 'video')
    })
    // dw end
  },
  destroyed() {
    if (this.player) {
      this.player.dispose()
    }
    window.removeEventListener('resize', this.updatePlayerStyle)
  }
}
</script>
<style>
.pcApp {
  /* 修复 pc 端 video 在一定分辨率下抖动问题 */
  overflow-y: scroll;
}
</style>
<style lang="scss" module>
.playerWrap {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 100%;
  user-select: none;
  -webkit-touch-callout: none;
}

.videoPlayer {
  width: 100%;
  min-width: 600px;
  margin: 0 150px;
}
</style>
